import React, { useState, useEffect } from 'react';

interface AIAdaptivePreviewProps {
  variant?: 'card' | 'featured';
}

const AIAdaptivePreview: React.FC<AIAdaptivePreviewProps> = ({ variant = 'card' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const pathNodes = [
    { id: 1, label: 'Assess', icon: '📊', active: false },
    { id: 2, label: 'Analyse', icon: '🧠', active: false },
    { id: 3, label: 'Adapt', icon: '🎯', active: false },
    { id: 4, label: 'Deliver', icon: '📚', active: false },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsProcessing(true);
      setTimeout(() => {
        setCurrentStep(prev => (prev + 1) % 4);
        setIsProcessing(false);
      }, 500);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const height = variant === 'featured' ? 'h-48' : 'h-32';

  return (
    <div
      className={`relative ${height} w-full rounded-xl overflow-hidden`}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-1.5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span className="text-[10px] font-semibold" style={{ color: '#94a3b8' }}>
          AI Learning Path
        </span>
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3" style={{ color: '#8b5cf6' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[9px]" style={{ color: '#8b5cf6' }}>AI-POWERED</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col justify-center h-[calc(100%-32px)]">
        {/* AI Flow Path */}
        <div className="flex items-center justify-between">
          {pathNodes.map((node, idx) => (
            <React.Fragment key={node.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    idx === currentStep ? 'scale-110' : ''
                  }`}
                  style={{
                    background: idx === currentStep
                      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)'
                      : idx < currentStep
                        ? 'rgba(16, 185, 129, 0.2)'
                        : 'rgba(255, 255, 255, 0.05)',
                    border: idx === currentStep
                      ? '2px solid #8b5cf6'
                      : idx < currentStep
                        ? '1px solid rgba(16, 185, 129, 0.3)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: idx === currentStep ? '0 0 20px rgba(139, 92, 246, 0.3)' : 'none',
                  }}
                >
                  <span className="text-base">{node.icon}</span>
                </div>
                <span
                  className="text-[9px] mt-1 font-medium"
                  style={{
                    color: idx === currentStep ? '#8b5cf6' : idx < currentStep ? '#10b981' : '#64748b',
                  }}
                >
                  {node.label}
                </span>
              </div>
              {idx < pathNodes.length - 1 && (
                <div className="flex-1 mx-1">
                  <div
                    className="h-0.5 rounded transition-all duration-500"
                    style={{
                      background: idx < currentStep
                        ? 'linear-gradient(90deg, #10b981, #10b981)'
                        : idx === currentStep && isProcessing
                          ? 'linear-gradient(90deg, #8b5cf6, rgba(139, 92, 246, 0.3))'
                          : 'rgba(255, 255, 255, 0.1)',
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Status text */}
        <div className="mt-3 text-center">
          <div
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
            style={{ background: 'rgba(139, 92, 246, 0.1)' }}
          >
            {isProcessing && (
              <div
                className="w-2 h-2 border border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#8b5cf6', borderTopColor: 'transparent' }}
              />
            )}
            <span className="text-[9px]" style={{ color: '#a78bfa' }}>
              {pathNodes[currentStep].label}ing student performance...
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAdaptivePreview;
